import { db } from "@/lib/firebase";
import {
  Persona,
  PersonaState,
  safePersonaParse,
} from "@/schemas/firestore/persona.schema";
import { PersonaGenerationService } from "@/lib/personaGenerationService";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { StateCreator } from "zustand";
import { StoreState } from "..";

export interface PersonaSlice extends PersonaState {
  /* actions */
  subscribeToPersonas: (orgId: string, domainId: string) => () => void;
  setSelectedPersona: (personaId: string | null) => void;
  /**
   * Toggle whether a persona feeds generation.
   *
   * The only persona write the client can make. Creation, editing and deletion
   * are backend-only (`allow write: if false` on the collection), so personas
   * are produced solely by the AI generation flow.
   */
  setPersonaEnabled: (
    domainId: string,
    personaId: string,
    isEnabled: boolean,
  ) => Promise<void>;
  clearPersonas: () => void;
  unsubscribePersonas: () => void;

  /* internals */
  _personaSubscriptions: Map<string, () => void>;
}

export const createPersonaSlice: StateCreator<
  StoreState,
  [],
  [],
  PersonaSlice
> = (set, get) => ({
  /* ---------- initial state ---------- */
  personas: [],
  selectedPersonaId: null,
  isLoading: false,
  error: null,
  _personaSubscriptions: new Map(),

  /* ---------- actions ---------- */
  subscribeToPersonas: (orgId: string, domainId: string) => {
    if (!orgId || !domainId) {
      console.log("Persona subscription requires orgId and domainId.");
      return () => {};
    }

    // Check if subscription already exists for this domain
    const existingUnsubscribe = get()._personaSubscriptions.get(domainId);
    if (existingUnsubscribe) {
      console.log(
        `Personas subscription already exists for domain ${domainId}`,
      );
      return existingUnsubscribe;
    }

    // Only set loading if no personas exist yet
    set((state) => ({
      isLoading: state.personas.length === 0,
      error: null,
    }));

    console.log(
      `Setting up real-time personas listener for domain ${domainId}`,
    );

    const personasRef = collection(
      db,
      `organizations/${orgId}/domains/${domainId}/personas`,
    );
    const q = query(personasRef, orderBy("created_at", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const personasList: Persona[] = [];
        snapshot.docs.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          const personaData = {
            // Spread first, override after — see the note in `ideaSlice`. Zod
            // strips anything outside `PersonaSchema`, so nothing extra leaks in.
            ...data,
            id: docSnapshot.id,
            user_id: data.user_id || get().currentUser?.uid || "",
            domain_id: domainId,
            is_enabled: data.is_enabled ?? true,
            name: data.name || "",
            tagline: data.tagline || "",
            pain: data.pain || "",
            journey_context: data.journey_context || "",
            fuel: data.fuel || [],
            friction: data.friction || [],
            quote: data.quote || "",
            demographics: data.demographics || {
              occupation: "",
              age_range: "",
              location: "",
              industry: "",
              company_size: "",
              budget_range: "",
            },
            custom_note: data.custom_note || "",
            status: data.status || "completed",
            created_at: data.created_at?.toDate() || new Date(),
            updated_at: data.updated_at?.toDate() || new Date(),
          };

          const result = safePersonaParse(personaData);
          if (result.success) {
            personasList.push(result.data);
          } else {
            console.error(
              `Validation failed for persona ${docSnapshot.id}:`,
              result.error.issues,
            );
          }
        });

        console.log(
          `Personas snapshot: ${snapshot.docs.length} docs, ${personasList.length} valid for domain ${domainId}`,
        );

        // Merge with personas of other domains
        set((state) => {
          const other = state.personas.filter((p) => p.domain_id !== domainId);
          return { personas: [...other, ...personasList], isLoading: false };
        });

        // Sync header tools with updated personas
        get().resetHeaderTools?.();
      },
      (error) => {
        console.error(
          `Personas subscription error for domain ${domainId}:`,
          error,
        );
        set({
          error: "Failed to subscribe to personas",
          isLoading: false,
        });
      },
    );

    // Wrap unsubscribe to remove from Map when called
    const wrappedUnsubscribe = () => {
      unsubscribe();
      get()._personaSubscriptions.delete(domainId);
      console.log(`Unsubscribed from personas for domain ${domainId}`);
    };

    // Store wrapped unsubscribe in Map keyed by domainId
    set((state) => {
      const newSubscriptions = new Map(state._personaSubscriptions);
      newSubscriptions.set(domainId, wrappedUnsubscribe);
      return { _personaSubscriptions: newSubscriptions };
    });

    return wrappedUnsubscribe;
  },

  setSelectedPersona: (personaId: string | null) => {
    set({ selectedPersonaId: personaId });
  },

  setPersonaEnabled: async (
    domainId: string,
    personaId: string,
    isEnabled: boolean,
  ) => {
    if (!domainId || !personaId) {
      throw new Error(
        "Domain ID and Persona ID are required to toggle a persona",
      );
    }

    try {
      // Backend-mediated: personas are `allow write: if false` in
      // firestore.rules, so this cannot be a direct Firestore write. The
      // subscription picks up the change and refreshes local state.
      await PersonaGenerationService.setPersonaEnabled(
        personaId,
        domainId,
        isEnabled,
      );
    } catch (error) {
      console.error("Error toggling persona:", error);
      set({ error: "Failed to update persona" });
      throw error;
    }
  },

  clearPersonas: () => {
    set({
      personas: [],
      selectedPersonaId: null,
      isLoading: false,
      error: null,
    });
  },

  unsubscribePersonas: () => {
    const subscriptions = get()._personaSubscriptions;
    console.log(
      `Unsubscribing from ${subscriptions.size} persona subscriptions`,
    );

    // Unsubscribe from all domain listeners
    subscriptions.forEach((unsubscribe, domainId) => {
      console.log(`Unsubscribing from personas for domain ${domainId}`);
      unsubscribe();
    });

    // Clear state
    set({
      personas: [],
      selectedPersonaId: null,
      _personaSubscriptions: new Map(),
    });
  },
});
